import type { Locale, MessageKey } from "@src/i18n/locale-catalog";
import type {
  ShareCopyOutcome,
  ShareDialog,
} from "@src/features/sharing/ShareDialogs";
import { appRootUrl } from "../locale-navigation";
import { encodeShareDocument } from "@src/features/sharing/codec/codec";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AppStore } from "../state/app-store";
import type { TransformationViewportHandle } from "@src/features/visualization/TransformationViewport";
import type { RefObject } from "react";
import { captureShareDocument } from "../share-workspace";
import { getPlaybackElapsed } from "@src/features/playback/playback";
import { localeMetadata } from "@src/i18n/locale-catalog";

const SHARE_ENCODING_TIMEOUT_MS = 5_000;

class ShareEncodingTimeoutError extends Error {
  override readonly name = "ShareEncodingTimeoutError";
}

export function useShareLink({
  store,
  scene,
  setDialog,
  locale,
  navigationAttemptRef,
}: {
  store: AppStore;
  scene: RefObject<TransformationViewportHandle | null>;
  setDialog: (dialog: ShareDialog | null) => void;
  locale: Locale;
  navigationAttemptRef: RefObject<number>;
}) {
  const [shareLink, setShareLink] = useState("");
  const [shareCopyOutcome, setShareCopyOutcome] =
    useState<ShareCopyOutcome>("manual");
  const [shareStatus, setShareStatus] = useState<MessageKey | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const activeShareRef = useRef<number | null>(null);
  const shareStatusTimeout = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (shareStatusTimeout.current !== null)
        window.clearTimeout(shareStatusTimeout.current);
    },
    [],
  );

  const flash = useCallback((key: MessageKey) => {
    if (shareStatusTimeout.current !== null)
      window.clearTimeout(shareStatusTimeout.current);
    setShareStatus(key);
    shareStatusTimeout.current = window.setTimeout(() => {
      shareStatusTimeout.current = null;
      setShareStatus(null);
    }, 2_000);
  }, []);

  const isCurrentShare = (navigation: number) =>
    navigation === navigationAttemptRef.current;

  const share = async () => {
    if (activeShareRef.current !== null) return;
    const navigation = ++navigationAttemptRef.current;
    activeShareRef.current = navigation;
    if (shareStatusTimeout.current !== null) {
      window.clearTimeout(shareStatusTimeout.current);
      shareStatusTimeout.current = null;
    }
    setShareStatus("sharing");
    setIsSharing(true);
    store.getState().cancelShareLoad();
    const state = store.getState();
    const elapsed =
      state.animation.status === "idle"
        ? 0
        : getPlaybackElapsed(
            state.animation,
            // The share callback runs from a user event, not during render.
            // eslint-disable-next-line react-hooks/purity
            performance.now(),
          );
    const cameras = scene.current?.getCameraSnapshots();
    const encodingTimeout = createShareEncodingTimeout();
    try {
      if (!cameras) throw new Error("Scene is not ready");
      const encoded = await Promise.race([
        encodeShareDocument(captureShareDocument(state, elapsed, cameras)),
        encodingTimeout.promise,
      ]);
      if (!isCurrentShare(navigation)) return;
      const metadata = localeMetadata.find(({ code }) => code === locale);
      if (!metadata) return;
      const url = new URL(metadata.path, appRootUrl());
      url.hash = `s=${encoded}`;
      setShareLink(url.href);
      setShareCopyOutcome("manual");
      setShareStatus(null);
      setDialog("share");
      void copyShareLink(url.href, navigation);
    } catch (error) {
      if (!isCurrentShare(navigation)) return;
      if (error instanceof ShareEncodingTimeoutError) {
        finishSharing(navigation);
        ++navigationAttemptRef.current;
        flash("shareTimedOut");
        return;
      }
      flash(isSharePayloadTooLarge(error) ? "shareTooLarge" : "copyFailed");
    } finally {
      encodingTimeout.clear();
      finishSharing(navigation);
    }
  };

  const finishSharing = (navigation: number) => {
    if (activeShareRef.current !== navigation) return;
    activeShareRef.current = null;
    setIsSharing(false);
    setShareStatus((status) => (status === "sharing" ? null : status));
  };

  const copyShareLink = async (url: string, navigation: number) => {
    try {
      await navigator.clipboard.writeText(url);
      if (!isCurrentShare(navigation)) return;
      setShareCopyOutcome("copied");
      flash("copied");
    } catch {
      if (!isCurrentShare(navigation)) return;
      setShareCopyOutcome("manual");
      flash("copyFailed");
    }
  };

  return { shareLink, shareCopyOutcome, shareStatus, isSharing, share };
}

function createShareEncodingTimeout(): {
  promise: Promise<never>;
  clear: () => void;
} {
  let timeout = 0;
  const promise = new Promise<never>((_, reject) => {
    timeout = window.setTimeout(() => {
      reject(new ShareEncodingTimeoutError("Sharing timed out"));
    }, SHARE_ENCODING_TIMEOUT_MS);
  });
  return {
    promise,
    clear: () => {
      window.clearTimeout(timeout);
    },
  };
}

function isSharePayloadTooLarge(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    error.code === "too-large"
  );
}
